import React, { useState } from "react";
import { NavLink } from "react-router-dom";
import clsx from "clsx";
import CircularProgress from "@material-ui/core/CircularProgress";
import IconButton from "@material-ui/core/IconButton";
import AddIcon from "@material-ui/icons/Add";
import DeleteIcon from "@material-ui/icons/Delete";
import WarningIcon from "@material-ui/icons/Warning";
// import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import DeleteIcon2 from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import ArrowForwardIcon from "@material-ui/icons/ArrowForward";
import DialogActions from "@material-ui/core/DialogActions";
import NotificationsService from "../../laco-common/services/notifications";
import UIModal from "../ui/UIModal";
import { UIPageTitle } from "../ui/UIPageTitle";
import AddEdit, { AddEditProps } from "./AddEdit";
import { UIStyledTooltip, UIButtonWithLoadingProgress } from "../ui/UIComponents";
import { PSAdminContent } from ".";
import { CustomAction } from "../../models/actions";
import { FormattedViewContext } from "../grid/colDef";
import { getFormattedDataForView } from "../grid/data-formatters";
import { UIDisplayFields } from "../grid/UIFormattedView";
import { ColDef } from "ag-grid-community";

export type ActionProps = {
	data?: any;
	onAdd?: () => Promise<any>;
	addName?: string;
	deleteName?: string;
	onEdit?: (data) => Promise<any>;
	onDelete?: (data) => Promise<any>;
	isSimpleDelete?: boolean;
	disableDelete?: boolean;
	disabledTitle?: string;
	itemName?: string;
	editEntityProps?: AddEditProps;
	detailUrl?: string;
	uid?: string;
	customActions?: CustomAction[];
	colDefs?: ColDef[];
	className?: string;
	skipRoles?: boolean;
};

export const UIActions: React.FC<ActionProps> = (props: ActionProps) => {
	const [confirmDeleteOpen, setConfirmDeleteOpen] = useState<boolean>(false);
	const [deleting, setDeleting] = useState<boolean>(false);
	const [loadingProps, setLoadingProps] = useState<{ [prop: string]: boolean }>({});
	const deleteName: string = props.deleteName || props.itemName;
	const deleteTitle = deleteName ? `Delete ${deleteName}?` : "Confirm Delete";
	const data = props.data;
	const noDelete: boolean = data?.noDelete;

	const Wrapper = props.skipRoles ? React.Fragment : PSAdminContent;

	return (
		<div className={clsx("ui-actions-container d-flex flex-nowrap align-items-center", props.className)}>
			<Wrapper uid={props.uid}>
				<>
					{!!props.onAdd && (
						<UIStyledTooltip title={`Add ${props.addName || ""}`}>
							<IconButton
								aria-label={`Add ${props.addName || ""}`}
								size="small"
								className="mr-2-5 ui-icon-btn-floating"
								onClick={() => props.onAdd()}
							>
								<AddIcon />
							</IconButton>
						</UIStyledTooltip>
					)}
					{!!data && !!props.onDelete && (
						<>
							<UIStyledTooltip
								title={
									props.disableDelete || noDelete
										? props.disabledTitle
										: `Delete ${props.itemName || ""}`
								}
							>
								<IconButton
									className={clsx(
										"mr-2-5 ui-icon-btn-floating",
										(props.disableDelete || noDelete) && "action-disabled"
									)}
									aria-label={`Delete ${props.itemName || ""}`}
									size="small"
									onClick={(e) => {
										setConfirmDeleteOpen(true);
									}}
									disabled={Boolean(props.disableDelete || noDelete)}
								>
									{deleting && <CircularProgress className="position-absolute" size={16} />}
									<DeleteIcon />
								</IconButton>
							</UIStyledTooltip>
							{!!confirmDeleteOpen && (
								<UIConfirmDialog
									pageTitle={deleteTitle}
									title={deleteTitle}
									// getDescription={() => undefined}
									confirmBtnTxt={
										<span className="d-inline-flex align-items-center">
											<DeleteIcon2 style={{ fontSize: "15px" }} className="mr-2" /> Delete
										</span>
									}
									omitWarning={props.isSimpleDelete}
									actionName="Deleting..."
									open={confirmDeleteOpen}
									onCancel={() => setConfirmDeleteOpen(false)}
									onConfirm={() => {
										setDeleting(true);
										return props.onDelete(props.data).finally(() => {
											const deleteMsg: string = props.itemName
												? `${props.itemName} deleted.`
												: "Deleted successfully";
											if (!props.isSimpleDelete) NotificationsService.success(deleteMsg);
											setConfirmDeleteOpen(false);
											setDeleting(false);
										});
									}}
									data={props.data}
									colDefs={props.colDefs}
								/>
							)}
						</>
					)}
					{!!props.editEntityProps && (
						<AddEdit
							{...props.editEntityProps}
							btnTemplate={
								<UIStyledTooltip title={`Edit ${props.itemName}`}>
									<IconButton
										aria-label={`Edit ${props.itemName}`}
										size="small"
										className="mr-2-5 ui-icon-btn-floating"
									>
										<EditIcon />
									</IconButton>
								</UIStyledTooltip>
							}
						/>
					)}

					{!!data && !!props.onEdit && !data.noEdit && (
						<UIStyledTooltip title={`Edit ${props.itemName}`}>
							<IconButton
								aria-label={`Edit ${props.itemName}`}
								size="small"
								className="mr-2-5 ui-icon-btn-floating"
								onClick={() => props.onEdit(data)}
							>
								<EditIcon />
							</IconButton>
						</UIStyledTooltip>
					)}
				</>
				{(props.customActions || []).map((action: CustomAction, i) => {
					const disabled: boolean = action.disabled && action.disabled(data);
					const title: string =
						disabled && action.disabledTitle
							? action.disabledTitle(data)
							: action.getTitle
							? action.getTitle(data)
							: action.title;
					const dynamicStateProp: string = `action_${i}`;
					return (
						(action.predicate ? action.predicate(data) : true) && (
							<UIStyledTooltip title={title} key={i}>
								<span className={clsx(disabled && "action-disabled")}>
									<IconButton
										aria-label={title}
										size="small"
										color={action.color}
										className="mr-2-5 ui-icon-btn-floating"
										disabled={loadingProps[dynamicStateProp] || disabled}
										onClick={(e) => {
											e.preventDefault();

											let updateLoadingProps = loadingProps ? { ...loadingProps } : {};
											updateLoadingProps[dynamicStateProp] = true;
											setLoadingProps(updateLoadingProps);
											action.callback(data).finally(() => {
												let updateLoadingProps = loadingProps ? { ...loadingProps } : {};
												updateLoadingProps[dynamicStateProp] = false;
												setLoadingProps(updateLoadingProps);
											});
										}}
									>
										<action.icon />
										{loadingProps[dynamicStateProp] && (
											<CircularProgress className="position-absolute" size={16} />
										)}
									</IconButton>
								</span>
							</UIStyledTooltip>
						)
					);
				})}
			</Wrapper>
			{!!props.detailUrl && (
				<UIStyledTooltip title={`Go to ${props.itemName}`}>
					<NavLink to={props.detailUrl}>
						<IconButton aria-label="Delete" size="small" className="ui-icon-btn-floating">
							<ArrowForwardIcon />
						</IconButton>
					</NavLink>
				</UIStyledTooltip>
			)}
		</div>
	);
};

type UIDialogProps = {
	open: boolean;
	title: string;
	pageTitle?: string;
	actionName: string;
	getDescription?: () => string | React.ReactNode;
	confirmBtnTxt?: string | React.ReactNode;
	onConfirm: () => Promise<any>;
	onCancel: () => any;
	children?: React.ReactNode;
	colDefs?: ColDef[];
	data?: any;
	omitWarning?: boolean;
};

const UIConfirmDialog: React.FC<UIDialogProps> = (props) => {
	const [confirming, setConfirming] = React.useState(false);
	return (
		<>
			{props.open && <UIPageTitle pageTitle={props.pageTitle} />}
			<UIModal
				title={
					<div className="d-inline-flex align-items-start">
						<WarningIcon className="text-warning mr-2 mt-1" style={{ fontSize: "1rem" }} />
						<div>
							{props.title || "Confirm Delete"}
							{!props.omitWarning && (
								<div className="text-gray my-1 text-sm">This action can't be undone.</div>
							)}
						</div>
					</div>
				}
				regTitle={true}
				pageTitle={props.title}
				open={true}
				onClose={() => props.onCancel()}
				showCloseIcon={true}
				style={{ maxWidth: "500px" }}
				noPadding={true}
				className="pb-3"
			>
				{!!props.colDefs && !!props.data && (
					<FormattedViewContext.Provider
						value={{
							viewData: getFormattedDataForView<any>(props.colDefs, props.data, undefined, "list"),
						}}
					>
						<UIDisplayFields cols={props.colDefs as any} omitFieldIfEmpty={true} />
					</FormattedViewContext.Provider>
				)}

				<DialogActions>
					<div className="d-flex justify-content-between mt-1">
						{/* <button
							onClick={(e) => {
								setConfirming(true);
								e.preventDefault();
								props.onConfirm().then(() => {
									setConfirming(false);
								});
							}}
							disabled={confirming}
							className={clsx("btn btn-primary btn-narrow mr-2", confirming && "btn-processing")}
							autoFocus
						>
							{props.confirmBtnTxt || "Confirm"}
						</button> */}
						<button
							onClick={(e) => {
								e.preventDefault();
								props.onCancel();
							}}
							className="btn btn-link btn-narrow mr-3"
						>
							Cancel
						</button>
						<UIButtonWithLoadingProgress
							actionName={props.actionName || "Saving..."}
							onClick={(e) => {
								setConfirming(true);

								return props.onConfirm().then(() => {
									setConfirming(false);
								});
							}}
							disabled={confirming}
							// className={clsx("btn btn-primary btn-narrow mr-2", confirming && "btn-processing")}
							autoFocus
							className="btn btn-primary"
							// autoFocus
						>
							{props.confirmBtnTxt || "Confirm"}
						</UIButtonWithLoadingProgress>
					</div>
				</DialogActions>
			</UIModal>
		</>
	);
};
